import { useEffect, useRef } from "react";

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  rootMargin?: string;
}

/**
 * Hook to implement infinite scroll with Intersection Observer
 * @param callback - Function to call when intersection is detected
 * @param options - Configuration options
 * @returns Ref to attach to the target element
 */
export function useInfiniteScroll(
  callback: () => void,
  options: UseInfiniteScrollOptions
) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          options.hasMore &&
          !options.isLoading
        ) {
          callback();
        }
      },
      {
        rootMargin: options.rootMargin || "200px",
      }
    );

    if (targetRef.current) {
      observerRef.current.observe(targetRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [options.hasMore, options.isLoading, callback, options.rootMargin]);

  return targetRef;
}
